// src/marketing/content/GenerationPanel.tsx
import { useState } from 'react'
import type { Attachment, Generation, GenerationAspectRatio, GenerationResolution } from '../../lib/types'
import { useAttachments } from '../../crm/hooks/useAttachments'
import {
  ASPECT_RATIOS, RESOLUTIONS, REFERENCE_LIMITS, GENERATION_STATUS_LABELS,
  classifyReference, countReferences, isActive, isSettled, modelLabel,
} from './generations'
import { useGenerations, useCreateGeneration, useCancelGeneration, useGenerationPolling } from './useGenerations'

const sectionStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px',
  padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem',
}
const sectionTitleStyle: React.CSSProperties = {
  margin: 0, fontSize: '0.8125rem', fontWeight: 600, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.04em',
}
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--muted)', marginBottom: '0.375rem' }
const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--elevated)', border: '1px solid var(--line-strong)', borderRadius: '7px',
  padding: '0.5rem 0.75rem', color: 'var(--fg)', fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none',
}
const row3: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }
const smallMuted: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--muted)' }

type Props = { contentId: string; defaultPrompt: string }

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
}

function ReferenceRow({ attachment, checked, onToggle }: { attachment: Attachment; checked: boolean; onToggle: () => void }) {
  const kind = classifyReference(attachment.contentType)
  const inputId = `gen-ref-${attachment.id}`
  return (
    <label htmlFor={inputId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: kind ? 'var(--fg)' : 'var(--dim)' }}>
      <input id={inputId} data-testid={inputId} type="checkbox" checked={checked} disabled={!kind} onChange={onToggle} />
      <span>{attachment.filename}</span>
      {kind ? <span style={smallMuted}>{kind}</span> : <span style={smallMuted}>format non accepté</span>}
    </label>
  )
}

function HistoryRow({ generation }: { generation: Generation }) {
  const g = generation
  return (
    <div data-testid={`gen-row-${g.id}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', padding: '0.625rem 0', borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'baseline', fontSize: '0.8125rem' }}>
        <span style={{ fontWeight: 600 }}>{GENERATION_STATUS_LABELS[g.status]}</span>
        <span style={smallMuted}>{modelLabel(g.model)}</span>
        <span style={smallMuted}>{g.params.duration} s, {g.params.resolution}, {g.params.aspectRatio}{g.params.generateAudio ? ', audio' : ''}</span>
        <span style={smallMuted}>{formatDate(g.createdAt)}</span>
      </div>
      <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--muted)', whiteSpace: 'pre-wrap' }}>{g.prompt}</p>
      {g.error && g.error !== 'output_download_failed' && (
        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--arancio)' }}>{g.error}</p>
      )}
      {g.error === 'output_download_failed' && (
        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--arancio)' }}>Vidéo prête chez Higgsfield, rapatriement en cours</p>
      )}
      {g.status === 'completed' && g.outputAttachmentId && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <video data-testid={`gen-video-${g.id}`} src={`/api/attachments/${g.outputAttachmentId}`} controls style={{ maxWidth: '320px', borderRadius: '7px', background: '#000' }} />
          <a href={`/api/attachments/${g.outputAttachmentId}`} download style={{ fontSize: '0.75rem', color: 'var(--arancio)' }}>Télécharger la vidéo</a>
        </div>
      )}
    </div>
  )
}

export function GenerationPanel({ contentId, defaultPrompt }: Props) {
  const { data: attachments = [] } = useAttachments('content', contentId)
  const { data: generations = [] } = useGenerations(contentId)
  const createGeneration = useCreateGeneration(contentId)
  const cancelGeneration = useCancelGeneration(contentId)

  const [prompt, setPrompt] = useState(defaultPrompt)
  const [duration, setDuration] = useState(5)
  const [resolution, setResolution] = useState<GenerationResolution>('720p')
  const [aspectRatio, setAspectRatio] = useState<GenerationAspectRatio>('9:16')
  const [generateAudio, setGenerateAudio] = useState(true)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  // On polle toute génération non réglée : une completed sans sortie rapatriée continue d'être suivie
  // (le serveur retente le téléchargement), sans pour autant bloquer une nouvelle soumission.
  const pendingFromList = generations.find((g) => !isSettled(g)) ?? null
  const { generation: active, timedOut } = useGenerationPolling(pendingFromList)
  const isBlocked = active !== null && isActive(active.status)

  const counts = countReferences(attachments, selectedIds)
  const overLimit = (Object.keys(REFERENCE_LIMITS) as Array<keyof typeof REFERENCE_LIMITS>).some((k) => counts[k] > REFERENCE_LIMITS[k])
  const durationValid = Number.isInteger(duration) && duration >= 4 && duration <= 15
  const canSubmit = prompt.trim().length > 0 && durationValid && !isBlocked && !createGeneration.isPending && !overLimit

  function toggle(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  async function handleSubmit() {
    setError(null)
    // Une pièce jointe supprimée entre la sélection et l'envoi ne doit pas partir au serveur.
    const known = new Set(attachments.map((a) => a.id))
    const referenceAttachmentIds = selectedIds.filter((id) => known.has(id))
    try {
      await createGeneration.mutateAsync({
        prompt: prompt.trim(), duration, resolution, aspectRatio, generateAudio, referenceAttachmentIds,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue.')
    }
  }

  const history = generations.map((g) => (active && g.id === active.id ? active : g))

  return (
    <section style={sectionStyle} data-testid="gen-panel">
      <p style={sectionTitleStyle}>Génération vidéo</p>

      <div>
        <p style={{ ...labelStyle, marginBottom: '0.5rem' }}>Références (pièces jointes du contenu)</p>
        {attachments.length === 0 ? (
          <p style={{ ...smallMuted, margin: 0 }}>Ajoute tes fichiers avec « Ajouter un fichier » ci-dessus (JPEG, PNG, WebP, GIF, MP4, WAV).</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {attachments.map((a) => (
              <ReferenceRow key={a.id} attachment={a} checked={selectedIds.includes(a.id)} onToggle={() => toggle(a.id)} />
            ))}
            <p style={{ ...smallMuted, margin: '0.25rem 0 0', color: overLimit ? 'var(--arancio)' : 'var(--muted)' }}>
              {`${counts.image}/${REFERENCE_LIMITS.image} images, ${counts.video}/${REFERENCE_LIMITS.video} vidéos, ${counts.audio}/${REFERENCE_LIMITS.audio} audios`}
            </p>
          </div>
        )}
      </div>

      <div>
        <label style={labelStyle} htmlFor="gen-prompt">Prompt</label>
        <textarea id="gen-prompt" data-testid="gen-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      <div style={row3}>
        <div>
          <label style={labelStyle} htmlFor="gen-duration">Durée (s, 4 à 15)</label>
          <input id="gen-duration" type="number" min={4} max={15} value={duration} onChange={(e) => setDuration(Number(e.target.value))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="gen-resolution">Résolution</label>
          <select id="gen-resolution" value={resolution} onChange={(e) => setResolution(e.target.value as GenerationResolution)} style={inputStyle}>
            {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="gen-ratio">Ratio</label>
          <select id="gen-ratio" value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value as GenerationAspectRatio)} style={inputStyle}>
            {ASPECT_RATIOS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <label htmlFor="gen-audio" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--fg)' }}>
        <input id="gen-audio" type="checkbox" checked={generateAudio} onChange={(e) => setGenerateAudio(e.target.checked)} />
        Générer l'audio
      </label>

      {isBlocked && active && (
        <div data-testid="gen-active-banner" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--elevated)', border: '1px solid var(--line)', borderRadius: '7px', padding: '0.5rem 0.75rem', fontSize: '0.8125rem' }}>
          <span>Génération en cours ({GENERATION_STATUS_LABELS[active.status].toLowerCase()})</span>
          {timedOut && <span style={smallMuted}>Toujours en cours côté Higgsfield, recharge la page plus tard.</span>}
          {active.status === 'queued' && (
            <button type="button" data-testid="gen-cancel"
              onClick={() => { setError(null); cancelGeneration.mutate(active.id, { onError: (err) => setError(err.message) }) }}
              disabled={cancelGeneration.isPending}
              style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--line)', borderRadius: '6px', padding: '0.25rem 0.625rem', color: 'var(--muted)', fontSize: '0.75rem', fontFamily: 'inherit', cursor: 'pointer' }}>
              Annuler la génération
            </button>
          )}
        </div>
      )}

      <div>
        <button type="button" data-testid="gen-submit" onClick={() => { void handleSubmit() }} disabled={!canSubmit}
          style={{ background: canSubmit ? 'var(--arancio)' : 'var(--dim)', border: 'none', borderRadius: '7px', padding: '0.5rem 1.25rem', color: '#fff', fontSize: '0.875rem', fontWeight: 600, fontFamily: 'inherit', cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
          {createGeneration.isPending ? 'Envoi…' : 'Générer'}
        </button>
        {error && <p data-testid="gen-error" style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--arancio)' }}>{error}</p>}
      </div>

      {history.length > 0 && (
        <div data-testid="gen-history">
          <p style={{ ...labelStyle, marginBottom: 0 }}>Historique</p>
          {history.map((g) => <HistoryRow key={g.id} generation={g} />)}
        </div>
      )}
    </section>
  )
}
